import React, { useEffect, useState } from 'react';
import { onboardingAPI } from '../api/onboardingAPI';
import { STORAGE_KEYS } from '../../../config/storageKeys';
import './LeavingYearPrompt.css';

export const LEAVING_YEAR_STEP_ID = 'leavingYear';

const wasDismissed = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.LEAVING_YEAR_PROMPT_DISMISSED) === 'true';
  } catch (e) {
    return false; // Storage unavailable: the prompt shows again, which is harmless.
  }
};

const rememberDismissed = () => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.LEAVING_YEAR_PROMPT_DISMISSED, 'true');
  } catch (e) {
    // Storage unavailable: dismissal lasts until this page is reloaded.
  }
};

/**
 * "When do you leave school?" for a school student whose membership has no
 * leaving year (GET /api/onboarding/state's needs_leaving_year) — students
 * ported from the old groups who finished onboarding before it asked.
 *
 * The options are the server's own leavingYear step from
 * GET /api/onboarding/steps, never a list kept here, and the answer is saved
 * like any onboarding answer. Dismissing hides it for this browser session.
 * Renders nothing if the catalogue has no leavingYear step.
 */
const LeavingYearPrompt = () => {
  const [dismissed, setDismissed] = useState(wasDismissed);
  const [step, setStep] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (dismissed) return undefined;
    let cancelled = false;
    onboardingAPI.getSteps()
      .then((catalogue) => {
        if (cancelled) return;
        setStep((catalogue?.steps || []).find((s) => s.id === LEAVING_YEAR_STEP_ID) || null);
      })
      .catch(() => {
        if (!cancelled) setError('We could not load the leaving years just now.');
      });
    return () => { cancelled = true; };
  }, [dismissed]);

  const dismiss = () => {
    rememberDismissed();
    setDismissed(true);
  };

  const choose = async (value) => {
    setSaving(true);
    setError('');
    try {
      await onboardingAPI.saveAnswer(LEAVING_YEAR_STEP_ID, value);
      setSaved(true);
    } catch {
      setError('That did not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (dismissed || saved || (!step && !error)) return null;

  return (
    <section className="leaving-year-prompt" aria-labelledby="leaving-year-prompt-title">
      <div className="leaving-year-prompt__head">
        <h2 id="leaving-year-prompt-title" className="leaving-year-prompt__title">When do you leave school?</h2>
        <button type="button" className="leaving-year-prompt__close" aria-label="Not now" onClick={dismiss}>×</button>
      </div>
      <p className="leaving-year-prompt__body">Your school uses this to put you in the right year group.</p>
      {error ? <p className="leaving-year-prompt__error" role="alert">{error}</p> : null}
      {step ? (
        <div className="leaving-year-prompt__options">
          {step.options.map((option) => (
            <button
              key={option.v}
              type="button"
              className="leaving-year-prompt__option"
              disabled={saving}
              onClick={() => choose(option.v)}
            >
              {option.l}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default LeavingYearPrompt;
